import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import TrustLogo from '@rover/icons/svg/trust-logo.svg';
import { Box, Display, Flex, Paragraph } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import PageLayout from '@rover/react-lib/src/components/layouts/PageLayout';
import inject from '@rover/utilities/injector';

import { HeadingBrand2StyledLink, StyledBulletedList } from '../brand2ColorStyles.constants';

import Ellipse from './images/ellipse.svg';

const ResponsivePageLayout = styled(PageLayout)`
  margin-bottom: ${({ theme }) => theme.space['16x']};
  ${MQ.XS.toString()} {
    margin-bottom: ${({ theme }) => theme.space['16x']};
  }
`;
const StyledBox = styled(Box)`
  margin-right: ${({ theme }) => theme.space['12x']};
  margin-bottom: ${({ theme }) => theme.space['0x']};

  h1 {
    display: none;
  }

  ${MQ.XS.toString()} {
    margin-right: ${({ theme }) => theme.space['0x']};
    margin-bottom: ${({ theme }) => theme.space['4x']};
    flex: 1 0 100%;
    display: inline-flex;
    h1 {
      display: block;
      margin-left: ${({ theme }) => theme.space['6x']};
    }
  }
`;
const { emergencyHotlineHours, veterinaryCareLimit } = inject('Rover.context', {});
const StyledTrustAndSafetyIcon = styled(TrustLogo)`
  height: 128px;
  width: 96px;
  overflow: visible;

  ${MQ.XS.toString()} {
    height: 70.67px;
    width: 53px;
    min-width: 53px;
  }
`;
const StyledStandaloneLink = styled.a`
  text-decoration: none;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const GuaranteeServiceDescription = (): JSX.Element => {
  const { t } = useLingui();

  const list = [
    {
      IconClass: Ellipse,
      title: t({
        message: `Up to ${veterinaryCareLimit} in vet care`,
        comment: '{veterinaryCareLimit} is the total amount limit for the veterinary care',
      }),
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t({
        message: `${emergencyHotlineHours} Support`,
        comment:
          "{emergencyHotlineHours} is '24/7' for en-us, en-ca, fr-ca and empty string for other languages",
      }),
      content: '',
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));

  return (
    <Flex justifyContent="center" data-testid="guarantee-service-description">
      <ResponsivePageLayout>
        <Flex flexWrap="wrap">
          <StyledBox>
            <StyledTrustAndSafetyIcon />
            <Display size="600">
              <Trans>The Rover Guarantee </Trans>
            </Display>
          </StyledBox>

          <Box flex={['100%', '10']}>
            <Box display={['none', 'block']}>
              <Display size="600">
                <Trans>The Rover Guarantee </Trans>
              </Display>
            </Box>
            <Box>
              <Paragraph
                textColor="primary"
                webProps={{ maxWidth: 'none' }}
                fontWeight="semibold"
                size="300"
                marginBottom="1x"
              >
                <Trans>
                  We care about the health and well-being of pets and want to give pet care
                  providers the confidence to act quickly in an emergency.
                </Trans>
              </Paragraph>

              <StyledBulletedList items={list} width="auto" horizontal />

              <Box mt="4x">
                <StyledStandaloneLink href="#file-a-claim">
                  <HeadingBrand2StyledLink size="300" mb="1x">
                    <Trans>How to file a claim</Trans>
                  </HeadingBrand2StyledLink>
                </StyledStandaloneLink>
              </Box>
            </Box>
          </Box>
        </Flex>
      </ResponsivePageLayout>
    </Flex>
  );
};

export default GuaranteeServiceDescription;
